/**
 * Confirming and disputing a report (brief §9).
 *
 * The Waze mechanic, and the reason the map does not fill up with things that were true for
 * ten minutes last Tuesday. Pure, like everything else in this directory: the same rules
 * decide in the browser whether to offer the buttons and on the server whether to accept the
 * answer, and there is exactly one copy of them.
 */

import type { Cell } from './h3.ts';
import { isNear } from './h3.ts';

/**
 * How long a report stays confirmable, measured from what it says it saw.
 *
 * `observed_at` and not `created_at`, and the difference is the whole point: confirming means
 * "I am standing here and it is still true". A report that waited three hours in an offline
 * outbox before reaching the server describes something three hours old, whatever the row's
 * creation time says. Anchoring on the arrival time would make a queued report confirmable
 * long after the crowd went home.
 */
export const CONFIRM_WINDOW_MS = 2 * 60 * 60 * 1000;

/** Confirming from the neighbouring cells too — `gridDisk(cell, 1)`, roughly a 350 m span. */
export const CONFIRM_RING = 1;

/**
 * How far before a report a verdict may claim to have been cast.
 *
 * Nothing can be confirmed before it happened, but two clocks are involved: an offline
 * confirmation is judged against its `queuedAt` corrected for skew (brief §14a.4), and that
 * correction is good to seconds rather than to milliseconds. A minute of slack absorbs the
 * arithmetic without admitting anything that could reasonably be called a time machine.
 */
export const CONFIRM_CLOCK_TOLERANCE_MS = 60 * 1000;

/** Disputes needed for the automatic hide, provided the balance is negative as well. */
export const AUTO_HIDE_DISPUTES = 3;

export type Verdict = 1 | -1;

export function isVerdict(value: unknown): value is Verdict {
  return value === 1 || value === -1;
}

export type Tally = { confirmed: number; disputed: number };

export type ConfirmSubject = {
  authorId: string;
  cell: Cell;
  observedAt: Date;
  hidden: boolean;
};

export type ConfirmRefusal =
  | 'own_sighting'
  | 'confirm_window_closed'
  | 'too_far_away'
  | 'sighting_hidden';

export type ConfirmDecision = { ok: true } | { ok: false; reason: ConfirmRefusal };

/**
 * May this person cast this verdict, right now, from there?
 *
 * The order of the checks is the order of the answers we are willing to give. "It is your own
 * report" and "the window is closed" are facts the caller already knows, so saying them costs
 * nothing. "You are too far away" is checked last because it is the only one that reveals
 * something — where the report is — and by then the caller has already been told the id
 * exists.
 */
export function canConfirm(
  subject: ConfirmSubject,
  viewerId: string,
  viewerCell: Cell,
  now: Date,
): ConfirmDecision {
  if (subject.hidden) return { ok: false, reason: 'sighting_hidden' };
  // The author cannot vouch for themselves; otherwise `w_trust` measures enthusiasm.
  if (subject.authorId === viewerId) return { ok: false, reason: 'own_sighting' };
  const age = now.getTime() - subject.observedAt.getTime();
  if (age > CONFIRM_WINDOW_MS) return { ok: false, reason: 'confirm_window_closed' };
  // And not before it happened. Without this a queued confirmation dated earlier than the
  // report it answers has a *negative* age, which passes an upper bound and would let a
  // backdated op confirm anything.
  if (age < -CONFIRM_CLOCK_TOLERANCE_MS) return { ok: false, reason: 'confirm_window_closed' };
  if (!isNear(viewerCell, subject.cell, CONFIRM_RING)) {
    return { ok: false, reason: 'too_far_away' };
  }
  return { ok: true };
}

/** German for each refusal — the API hands these straight to the user. */
export const REFUSAL_MESSAGES: Record<ConfirmRefusal, string> = {
  own_sighting: 'Die eigene Meldung kann man nicht bestätigen.',
  confirm_window_closed:
    'Diese Meldung ist älter als zwei Stunden und lässt sich nicht mehr bestätigen.',
  too_far_away: 'Dafür musst du in der Nähe sein.',
  sighting_hidden: 'Diese Meldung ist ausgeblendet.',
};

/**
 * The automatic hide from §9: three disputes **and** a negative balance.
 *
 * Both halves matter. Three disputes alone would let a trio hide a report that eight other
 * people confirmed, which is a brigading tool rather than a moderation rule. A negative
 * balance alone would hide a report on the strength of one dispute and no confirmations,
 * which is a heckler's veto.
 */
export function shouldAutoHide(tally: Tally): boolean {
  return tally.disputed >= AUTO_HIDE_DISPUTES && tally.disputed > tally.confirmed;
}

/**
 * `w_trust(s)` from brief §8: `clamp(1 + 0.25·confirmed − 0.35·disputed, 0.2, 2.0)`.
 *
 * It lives here rather than in `gdi.ts` because it is a property of the confirmations, and
 * because the asymmetry — a dispute weighs more than a confirmation — is a §9 decision. A
 * report nobody has touched keeps a weight of exactly 1, so the common case is unaffected.
 */
export function trustWeight(tally: Tally): number {
  const raw = 1 + 0.25 * tally.confirmed - 0.35 * tally.disputed;
  return Math.min(2, Math.max(0.2, raw));
}
